/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import { Button, Grid, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';
import type { GridProps } from '@coursera/cds-core';

import {
  DEFAULT_GRID_BREAKPOINTS_CONFIG,
  PAGE_HEADER_HEIGHT,
} from 'bundles/cds-labs/components/ShowMoreGridSection/constants';

import _t from 'i18n!nls/cds-labs';

type GridSize = NonNullable<Exclude<GridProps['lg'], boolean>>;
type GridSpacing = GridProps['spacing'];

export const styles = {
  container: css`
    padding: var(--cds-spacing-400) 0;
  `,

  titleSection: css`
    margin-bottom: var(--cds-spacing-100);
  `,

  itemList: css`
    list-style: none;
    padding: 0;
  `,
  buttonsWrapper: css`
    gap: var(--cds-spacing-200);
    margin-top: var(--cds-spacing-150);
    align-items: center;
    display: flex;

    ${breakpoints.down('xs')} {
      flex-direction: column;
      gap: var(--cds-spacing-100);

      && > * {
        width: 100%;
        max-width: 100%;
      }
    }
  `,
};

// define the requirement of a basic item props
export type BaseItem = { id: string };
export type renderItemProps<Item extends BaseItem> = {
  item: Item;
  itemIndex: number;
  itemRef?: React.RefObject<HTMLAnchorElement>;
  isExpandedCollection?: boolean;
  defaultCardsCount?: number;
};
type NumberGridSize = Exclude<GridSize, 'auto'>;
export type GridConfig = { lg?: NumberGridSize; md: NumberGridSize; sm: NumberGridSize; xs: NumberGridSize };
export type ExpandButtonProps = {
  onClick: () => void;
  onKeyDown: () => void;
  showMoreCount: number;
  isExpanded: boolean;
  defaultLabel: string;
  buttonRef?: React.RefObject<HTMLButtonElement>;
};

export type ShowMoreGridSectionProps<Item extends BaseItem> = {
  className?: string;

  /**
   * Number of default showed row before expanding.
   * @default 1
   */
  defaultRows?: number;

  /**
   * Max number of items to show for every progressive expansion
   * @default 8
   */
  showMoreAmount?: number;

  /**
   * Defines the space between the item components.
   */
  spacing?: GridSpacing;

  /**
   * Array of items to show in the collection. Item is type parameter defined when using.
   */
  items: Item[];

  /**
   * Optional prop to update items Grid Config for responsiveness.
   * @default DEFAULT_GRID_BREAKPOINTS_CONFIG
   */
  itemsGridConfig?: GridConfig;

  /**
   * Function that will be called every time the collection expands and completely expanded
   * @param isExpanded whether the collection is expanded
   * @param isCompletelyExpanded whether the collection is fully expanded i.e. all items are visible
   */
  onExpanded?: ({ isExpanded, isCompletelyExpanded }: { isExpanded: boolean; isCompletelyExpanded: boolean }) => void;

  /**
   * Function that return a rendered title.
   */
  renderTitle?: () => React.ReactNode;

  /**
   * Function that return a rendered sub section.
   */
  renderSubSection?: () => React.ReactNode;

  /**
   * Function that return a rendered footer.
   */
  renderFooter?: () => React.ReactNode;

  /**
   * Function that return a rendered expand button.
   */
  renderExpandButton?: ({ onClick, showMoreCount, isExpanded, defaultLabel }: ExpandButtonProps) => React.ReactNode;

  /**
   * Function that will be called when the expand button is clicked.
   * @param numberOfItemsExpanded number of items that will be expanded
   * @param cardsPerRow number of cards per row
   * @param numberOfItemsDisplayed number of items currently displayed
   */
  onExpandButtonClick?: ({
    numberOfItemsExpanded,
    cardsPerRow,
    numberOfItemsDisplayed,
  }: {
    numberOfItemsExpanded: number;
    cardsPerRow: number;
    numberOfItemsDisplayed: number;
  }) => void;

  /**
   * Function that return a rendered secondary CTA.
   */
  renderSecondaryCta?: () => React.ReactNode;

  /**
   * Function that return a rendered item.
   */
  renderItem: ({ item, itemIndex }: renderItemProps<Item>) => React.ReactNode;

  /**
   * Title to display.
   */
  title?: string;

  /**
   * To ensure component renders correctly on mobile screen sizes, pass in a boolean that should be true if we should render mobile breakpoint
   * options in SSR (like for example, checking "isMobileBrowser" via userAgent).
   */
  defaultMobileBreakpointMatches?: boolean;

  /**
   * Function that returns a rendered empty state when items array is empty.
   * If not provided and items are empty, the component will return null (render nothing).
   * This allows for custom empty state messaging, calls-to-action, or placeholder content.
   * Note: The returned content will be rendered inside an <li> element.
   */
  renderEmptyItems?: () => React.ReactNode;
};

function ExpandButton({ onClick, defaultLabel, onKeyDown, buttonRef }: ExpandButtonProps) {
  return (
    <Button ref={buttonRef} size="small" variant="secondary" onKeyDown={onKeyDown} onClick={onClick}>
      {defaultLabel}
    </Button>
  );
}

/**
 * ShowMoreGridSection provides a way to show more collection of items that is in Grid (responsive) format
 * with the addition of title, sub section, and footer.
 *
 *
 * @example
 * <ShowMoreGridSection
 *  title="ShowMoreGridSection with custom footer"
 *  items={items.slice(0, 4)}
 *  itemsGridConfig={{ xs: 6, sm: 3, md: 2 }}
 *  renderItem={renderItem}
 *  renderTitle={() => <a href="https://www.coursera.org">ShowMoreGridSection with custom title (link)</a>}
 *  renderSubSection={() => <Typography variant="body2" color="success">
 *      custom sub section component
 *    </Typography>
 *  }
 *  renderExpandButton={() => <a href="https://www.coursera.org">link to page</a>}
 *  renderFooter={() => <Typography variant="body2" color="success">
 *      custom footer component
 *    </Typography>
 *  }
 * />
 */
export function ShowMoreGridSection<Item extends BaseItem>({
  renderTitle,
  renderSubSection,
  renderFooter,
  renderExpandButton,
  onExpandButtonClick,
  renderSecondaryCta,
  renderEmptyItems,
  onExpanded,
  items,
  renderItem,
  itemsGridConfig = DEFAULT_GRID_BREAKPOINTS_CONFIG,
  defaultRows = 1,
  title,
  showMoreAmount = 8,
  spacing,
  defaultMobileBreakpointMatches,
  ...props
}: ShowMoreGridSectionProps<Item>) {
  const isMobile = useMediaQuery(breakpoints.down('xs'), {
    defaultMatches: defaultMobileBreakpointMatches,
  });
  const isTablet = useMediaQuery(breakpoints.down('sm'));
  const isWideScreen = useMediaQuery(breakpoints.up('lg'));
  const [isExpanded, setIsExpanded] = useState(false);
  const hasCalledOnExpanded = useRef(false);
  const sectionRef = useRef<HTMLElement>(null);
  const firstExpandedRef = useRef<HTMLAnchorElement>(null);
  const expandButtonRef = useRef<HTMLButtonElement>(null);
  const shouldFocusExpandButton = useRef(false);
  let cardsPerRow = 12 / itemsGridConfig.md;
  if (isTablet) cardsPerRow = 12 / itemsGridConfig.sm;
  if (isMobile) cardsPerRow = 12 / itemsGridConfig.xs;
  if (isWideScreen && itemsGridConfig.lg) cardsPerRow = 12 / itemsGridConfig.lg;
  const defaultCardsCount = cardsPerRow * defaultRows;
  const [numItemsToDisplay, setNumItemsToDisplay] = useState(defaultCardsCount);
  const [itemIndexToFocus, setitemIndexToFocus] = useState(defaultCardsCount);
  const itemsToShow = items.slice(0, numItemsToDisplay);
  const itemsLeft = items.length - itemsToShow.length;
  const showMoreCount = Math.min(showMoreAmount, itemsLeft);
  const isCompletelyExpanded = itemsLeft === 0;
  const showExpandButton = items.length > defaultCardsCount;
  const expandButtonLabel = isCompletelyExpanded
    ? _t('Show fewer')
    : _t('Show #{showMoreCount} more', { showMoreCount });
  let renderedTitle;

  useEffect(() => {
    if (shouldFocusExpandButton.current) {
      // After collapse, focus the first card for reliable focus management.
      // Focusing the expand button is unreliable due to render prop timing.
      shouldFocusExpandButton.current = false;
      firstExpandedRef?.current?.focus?.();
      // Reset so next expansion focuses the first NEW card, not card 0
      setitemIndexToFocus(defaultCardsCount);
    } else if (isExpanded && firstExpandedRef?.current) {
      // Always move focus to the first new item after expansion
      // This ensures proper focus management for both keyboard users
      // and screen reader users (JAWS, VoiceOver) using virtual cursor navigation
      firstExpandedRef?.current?.focus?.();
      if (isCompletelyExpanded) {
        setitemIndexToFocus(defaultCardsCount);
      } else {
        setitemIndexToFocus(numItemsToDisplay);
      }
    }
    if (numItemsToDisplay !== defaultCardsCount && !isExpanded) {
      setNumItemsToDisplay(defaultCardsCount);
    }
  }, [isExpanded, firstExpandedRef, numItemsToDisplay, defaultCardsCount, isCompletelyExpanded]);
  useEffect(() => {
    // reset expanded state if items changed
    setIsExpanded(false);
    setNumItemsToDisplay(defaultCardsCount);
  }, [items, defaultCardsCount]);
  useEffect(() => {
    if (isCompletelyExpanded && !hasCalledOnExpanded.current) {
      onExpanded?.({ isExpanded, isCompletelyExpanded });
      hasCalledOnExpanded.current = true;
    } else if (!isCompletelyExpanded) {
      onExpanded?.({ isExpanded, isCompletelyExpanded });
      hasCalledOnExpanded.current = false;
    }
  }, [onExpanded, isExpanded, isCompletelyExpanded, hasCalledOnExpanded]);

  const isItemsEmpty = !items || items.length === 0;

  if (isItemsEmpty && !renderEmptyItems) {
    return null;
  }

  if (renderTitle) {
    renderedTitle = renderTitle();
  } else if (title) {
    renderedTitle = (
      <Typography2 variant="subtitleLarge" component="h2">
        {title}
      </Typography2>
    );
  }

  const buttonOnClick = () => {
    if (!isExpanded) setIsExpanded(true);
    if (isCompletelyExpanded) {
      // Set flag so useEffect focuses the first card after re-render.
      shouldFocusExpandButton.current = true;
      // scroll back to the top of the collection
      const top = (sectionRef.current?.getBoundingClientRect().y || 0) - PAGE_HEADER_HEIGHT;
      window.scrollBy({ top, behavior: 'smooth' });
      setitemIndexToFocus(0);
      setNumItemsToDisplay(defaultCardsCount);
      setIsExpanded(false);
    } else {
      onExpandButtonClick?.({
        numberOfItemsExpanded: showMoreCount,
        cardsPerRow,
        numberOfItemsDisplayed: numItemsToDisplay,
      });
      setNumItemsToDisplay(numItemsToDisplay + showMoreCount);
    }
  };

  const expandButtonProps = {
    onClick: buttonOnClick,
    showMoreCount,
    isExpanded,
    defaultLabel: expandButtonLabel,
    onKeyDown: () => undefined,
    buttonRef: expandButtonRef,
  };

  const secondaryCta = renderSecondaryCta ? renderSecondaryCta() : null;

  return (
    <section css={styles.container} ref={sectionRef} {...props}>
      {(renderedTitle || renderSubSection) && (
        <div css={styles.titleSection}>
          {renderedTitle}
          {renderSubSection?.()}
        </div>
      )}
      <Grid container component="ul" spacing={spacing} css={styles.itemList}>
        {isItemsEmpty && renderEmptyItems ? (
          <Grid item {...itemsGridConfig} component="li">
            {renderEmptyItems()}
          </Grid>
        ) : (
          itemsToShow.map((item, itemIndex) => (
            <Grid key={item.id} item {...itemsGridConfig} component="li">
              {renderItem({
                item,
                itemIndex,
                itemRef: itemIndex === itemIndexToFocus ? firstExpandedRef : undefined,
                isExpandedCollection: isExpanded,
                defaultCardsCount,
              })}
            </Grid>
          ))
        )}
      </Grid>
      {(showExpandButton || secondaryCta) && (
        <div className="ShowMoreGridSection-button-wrapper" css={styles.buttonsWrapper}>
          {renderExpandButton && showExpandButton ? renderExpandButton(expandButtonProps) : null}
          {!renderExpandButton && showExpandButton ? <ExpandButton {...expandButtonProps} /> : null}
          {secondaryCta}
        </div>
      )}
      {renderFooter?.()}
    </section>
  );
}

export default ShowMoreGridSection;
